import { BaseModel } from '@/models/BaseModel';
import { ContentColumnData } from '@/typings/interfaces/data/grid/column';
import { AddonLfTextModel } from '@/components/addons/lf-text/Model';
import { AddonLfImageModel } from '@/components/addons/lf-image/Model';
import { AddonGameflowModel } from '@/components/addons/gameflow/Model';
import { AddonLfHeadlineModel } from '@/components/addons/lf-headline/Model';
import { AddonLfAnimatedHeadlineModel } from '@/components/addons/lf-animated-headline/Model';
import { AddonLfButtonModel } from '@/components/addons/lf-button/Model';
import { AddonLfIconModel } from '@/components/addons/lf-icon/Model';
import { AddonHTMLModel } from '@/components/addons/html-element/Model';
import { SettingsModel } from '@/components/layout/SettingsModel';
import ActionsModel from '@/models/actions/ActionsModel';
import { AddonLfDividerModel } from '@/components/addons/lf-divider/Model';
import { ColumnSettingsModel } from '@/components/layout/column/ColumnSettingsModel';
import { AddonAccordionModel } from '@/components/addons/accordion/Model';
import { AddonRSSModel } from '@/components/addons/rss/Model';
import { AddonShareModel } from '@/components/addons/share/Model';
import { AddonIframeElementModel } from '@/components/addons/iframe-element/Model';
import { AddonCountdownModel } from '@/components/addons/countdown/Model';
import { AddonContactCTAModel } from '@/components/addons/contact-cta/Model';
import { AddonGoogleMapsModel } from '@/components/addons/googlemap/Model';
import { AddonLFShareModel } from '@/components/addons/lf-share/Model';
import { AddonMenuModel } from '@/components/addons/menu/Model';
import { AddonPrizesModel } from '@/components/addons/prizes/Model';
import { AddonRegistrationModel } from '@/components/addons/registration/Model';
import { AddonSliderModel } from '@/components/addons/slider/Model';
import { AddonSlideShowModel } from '@/components/addons/slideshow/Model';
import { AddonSponsorsModel } from '@/components/addons/sponsors/Model';
import { AddonTipAFriendModel } from '@/components/addons/tipafriend/Model';
import { AddonTrustPilotModel } from '@/components/addons/trustpilot/Model';
import { AddonVideoEmbedModel } from '@/components/addons/video-embed/Model';
import { AddonGameplayModel } from '@/components/addons/gameplay/Model';
import { AddonStepGuideModel } from '@/components/addons/stepguide/Model';
import { AddonGigyaModel } from '@/components/addons/gigya/Model';
import { AddonQuestionValidationModel } from '@/components/addons/question-validation/Model';
import { AddonAuthenticationModel } from '@/components/addons/authentication/Model';
import { AddonAgeGateModel } from '@/components/addons/age-gate/Model';
import { AddonELearningModel } from '@/components/addons/elearning/Model';
import RowModel from '@/components/layout/row/RowModel';
import { AdvancedAddonsStyleData, CustomCSS } from '@/typings/interfaces/data/settings/settings';
import { CustomCSSModel } from '@/models/CustomCSSModel';
import { getDeviceData } from '@/hooks/useDevice';
import store from '@/store';
import { SectionType } from '@/typings/enums/enums';
import { AddonAddWishModel } from '@/components/addons/addwish/Model';
import { createEditFormUrl, generateUniqueId } from '@/utilities/Utilities';
import { AddonIFrameGamesModel } from '@/components/addons/iframe-games/Model';
import { ReplacementTags } from '@/store/modules/global/state';
import AddonModel from '@/models/grid/AddonModel';
import { AddonSocialShareModel } from '@/components/addons/social-share/Model';
import { AddonLeaderboardModel } from '@/components/addons/leaderboard/Model';

interface NewAddon {
  model: ContentAddonType;
  id: number;
}

interface ContentAddonEditState {
  isFocus?: boolean;
  isActive?: boolean;
  isCollapsed?: boolean;
}

// eslint-disable-next-line
const AddonMapping: Record<string, any> = {
  addwish: AddonAddWishModel,
  countdown: AddonCountdownModel,
  'iframe-element': AddonIframeElementModel,
  'iframe-games': AddonIFrameGamesModel,
  share: AddonShareModel,
  rss: AddonRSSModel,
  accordion: AddonAccordionModel,
  'lf-divider': AddonLfDividerModel,
  'lf-headline': AddonLfHeadlineModel,
  'lf-text': AddonLfTextModel,
  'lf-image': AddonLfImageModel,
  'lf-animated-headline': AddonLfAnimatedHeadlineModel,
  'lf-button': AddonLfButtonModel,
  'lf-icon': AddonLfIconModel,
  'html-element': AddonHTMLModel,
  'contact-cta': AddonContactCTAModel,
  googlemap: AddonGoogleMapsModel,
  'lf-share': AddonLFShareModel,
  menu: AddonMenuModel,
  prizes: AddonPrizesModel,
  registration: AddonRegistrationModel,
  slider: AddonSliderModel,
  slideshow: AddonSlideShowModel,
  sponsors: AddonSponsorsModel,
  tipafriend: AddonTipAFriendModel,
  trustpilot: AddonTrustPilotModel,
  'video-embed': AddonVideoEmbedModel,
  stepguide: AddonStepGuideModel,
  gigya: AddonGigyaModel,
  'question-validation': AddonQuestionValidationModel,
  authentication: AddonAuthenticationModel,
  'age-gate': AddonAgeGateModel,
  elearning: AddonELearningModel,
  gameflow: AddonGameflowModel,
  gameplay: AddonGameplayModel,
  'social-share': AddonSocialShareModel,
  leaderboard: AddonLeaderboardModel
};

export interface ContentAddon {
  id?: string;
  alias: string;
  label?: string;
  edit?: ContentAddonEditState;
  model: ContentAddonType;
  settings?: {
    advanced?: AdvancedAddonsStyleData;
  };
}

export type ContentAddonType =
  | AddonLeaderboardModel
  | AddonAddWishModel
  | AddonCountdownModel
  | AddonIframeElementModel
  | AddonShareModel
  | AddonRSSModel
  | AddonIFrameGamesModel
  | AddonAccordionModel
  | AddonLfDividerModel
  | AddonLfTextModel
  | AddonLfAnimatedHeadlineModel
  | AddonLfHeadlineModel
  | AddonLfImageModel
  | AddonLfButtonModel
  | AddonLfIconModel
  | AddonHTMLModel
  | AddonContactCTAModel
  | AddonGoogleMapsModel
  | AddonLFShareModel
  | AddonMenuModel
  | AddonPrizesModel
  | AddonRegistrationModel
  | AddonSliderModel
  | AddonSlideShowModel
  | AddonSponsorsModel
  | AddonTipAFriendModel
  | AddonTrustPilotModel
  | AddonVideoEmbedModel
  | AddonGigyaModel
  | AddonStepGuideModel
  | AddonELearningModel
  | AddonAgeGateModel
  | AddonAuthenticationModel
  | AddonQuestionValidationModel
  | AddonGameplayModel
  | AddonGameflowModel
  | AddonSocialShareModel;

interface ColumnEditState {
  isCollapsed: boolean;
  isActive: boolean;
  isFocus: boolean;
  hasActiveAddon: boolean;
}

interface ColumnConfigState {
  hasRegistrationAddon: boolean;
}

export interface ColumnModelState {
  modelId: string;
  id: string;
  classIdentifier: string;
  size: number;
  originalSize: number;
  edit?: ColumnEditState;
  label: string;
  addons: ContentAddonType[];
  settings: SettingsModel;
  action?: ActionsModel;
  config?: ColumnConfigState;
  customCss: CustomCSSModel | undefined;
}

export default class ColumnModel extends BaseModel<ContentColumnData, ColumnModelState> {
  public sectionId: number | undefined;
  public campaignId: number;
  public row: RowModel;
  public replacementTags?: ReplacementTags;
  private deleted = false;

  private static reservedIds: string[] = [];

  constructor(data: ContentColumnData, row: RowModel) {
    super(data);
    this.row = row;
    this.campaignId = row.campaignId;

    if (row.section) {
      this.sectionId = row.section.state.id;
    }

    // Conflict found or no column id exists. Some other columns already uses this ID.
    // We need to refresh it to avoid conflicts with style or other functionality.
    if (!data.id || ColumnModel.reservedIds.includes(data.id)) {
      data.id = generateUniqueId();
    }

    ColumnModel.reservedIds.push(data.id);

    this.parse(data);
  }

  getValueRoot() {
    return 'settings';
  }

  getTotalAddons() {
    return this.getData().addons.length;
  }

  parse(data: ContentColumnData) {
    const state = this.state;
    state.modelId = this.modelId;
    state.id = data.id ?? generateUniqueId();
    state.classIdentifier = `grid__col--${state.id}`;
    state.label = data.settings?.basic?.name || 'Column';
    state.size = state.originalSize = Number(data.size);

    const editState = store.getters['campaign/state'].edit;
    if (!state.edit && editState?.enabled) {
      state.edit = {
        isActive: false,
        isFocus: false,
        isCollapsed: true,
        hasActiveAddon: false
      };
    }

    state.config = {
      hasRegistrationAddon: ColumnModel.checkForAddons(data, 'registration')
    };

    const addonsMap: { [key: string]: ContentAddonType } = {};

    if (state.addons) {
      state.addons.forEach((addon, addonIndex) => {
        addonsMap[`${addonIndex}${addon.alias}`] = addon;
      });
    }

    state.addons = data.addons
      .filter((addon) => !!addon.alias)
      .map((addon, addonIndex: number) => {
        if (typeof AddonMapping[addon.alias] === 'undefined') {
          throw new Error(`Unrecognized addon "${addon.alias}"`);
        }

        const id = `${addonIndex}${addon.alias}`;

        // eslint-disable-next-line security/detect-object-injection
        if (typeof addonsMap[id] !== 'undefined') {
          // TODO: Investigate if we'll be able to do a work-around that does not require a ignore
          // @ts-ignore
          // eslint-disable-next-line security/detect-object-injection
          addonsMap[id].setData(addon);
          // eslint-disable-next-line security/detect-object-injection
          return addonsMap[id];
        }
        return new AddonMapping[addon.alias](addon, this);
      });

    if (state.settings) {
      state.settings.setData(data.settings ?? {});
    } else {
      state.settings = new ColumnSettingsModel(data.settings ?? {}, this.getSection());
    }
    if (data?.settings?.basic?.action && data?.settings?.basic?.action?.type) {
      state.action = new ActionsModel(data.settings.basic.action);
    }

    if (data.settings?.advanced?.customcss) {
      state.customCss = ColumnModel.parseCustomCSSData(data, state.classIdentifier);
    }
  }

  public getSection() {
    return this.row.section;
  }

  public getEditUrl() {
    const editFormUrl = `/edit/campaign/column/${this.campaignId}/${this.sectionId}?row_index=${this.rowIndex}&col_index=${this.index}`;
    return createEditFormUrl(editFormUrl);
  }

  reload() {
    this.row.section.reload();
  }

  setHasActiveAddon(hasActiveAddon: boolean) {
    if (this.state.edit) {
      this.state.edit.hasActiveAddon = hasActiveAddon;
    }
  }

  canDelete(): boolean {
    if (this.index === 0) {
      return false;
    }

    return this.row.state.columns.length >= 1;
  }

  canOpenSectionSettings() {
    return this.getSection().getSectionType() === SectionType.FLOWPAGE;
  }

  canOpenGameSettings() {
    return this.getSection().getSectionType() === SectionType.FLOWPAGE;
  }

  canCopy(): boolean {
    return true;
  }

  canDuplicate(): boolean {
    const row = this.row;
    const rowState = row.state;

    const hasRegistrationAddon = this.getAddons<AddonRegistrationModel>('registration').length > 0;

    return !(rowState.columns && rowState.columns.length >= 4) && !hasRegistrationAddon;
  }

  canPaste(): boolean {
    const clipboardState = store.getters['clipboard/state'];
    const row = this.row;
    const rowState = row.state;
    if (clipboardState.elementInClipboard instanceof ColumnModel) {
      const parentSection = this.getSection();
      if (!parentSection.state.config.hasRegistrationAddon) {
        return true;
      } else {
        const model = clipboardState.elementInClipboard;
        if (model && model.state && model.state.config) {
          return !model.state.config.hasRegistrationAddon;
        }
      }
    } else if (
      clipboardState.elementInClipboard instanceof ColumnModel ||
      clipboardState.elementInClipboard instanceof AddonModel
    ) {
      if (this.state.config) {
        return !this.state.config.hasRegistrationAddon;
      }
    }
    return (
      (clipboardState.elementInClipboard instanceof ColumnModel && rowState.columns && rowState.columns.length <= 4) ||
      clipboardState.elementInClipboard instanceof AddonModel
    );
  }

  canMoveUp(): boolean {
    return this.index > 0;
  }

  onMoveUp() {
    this.row.onMoveColumnUp(this.index);
  }

  canMoveDown(): boolean {
    const row = this.row;
    const rowState = row.state;

    return rowState.columns.length - 1 > this.index;
  }

  onMoveDown() {
    this.row.onMoveColumnDown(this.index);
  }

  onMoveAddonUp(currentAddonIndex: number) {
    const data = this.getData();
    const addons = data.addons;
    const currentItem = addons[Number(currentAddonIndex)];
    addons[Number(currentAddonIndex)] = addons[currentAddonIndex - 1];
    addons[currentAddonIndex - 1] = currentItem;
    data.addons = addons;
    this.parse(data);
    store.dispatch('editing/setActiveModel', {
      model: this.state.addons[currentAddonIndex - 1]
    });
  }

  onMoveAddonDown(currentAddonIndex: number) {
    const data = this.getData();
    const addons = data.addons;
    const currentItem = addons[Number(currentAddonIndex)];
    addons[Number(currentAddonIndex)] = addons[currentAddonIndex + 1];
    addons[currentAddonIndex + 1] = currentItem;
    data.addons = addons;
    this.parse(data);
    store.dispatch('editing/setActiveModel', {
      model: this.state.addons[currentAddonIndex + 1]
    });
  }

  onPasteElement() {
    const data = store.getters['clipboard/data'];
    const clipboardState = store.getters['clipboard/state'];
    if (clipboardState.elementInClipboard instanceof AddonModel) {
      this.onPasteAddon(data, this.index);
    } else if (data) {
      this.row.onPasteColumn(data, this.index);
    }
  }

  async onPasteAddon(addonData: string, addonIndex: number) {
    const columnData = this.getData();
    const clone = JSON.parse(addonData) as ContentAddon;
    clone.id = generateUniqueId();

    /**
     * If we paste in a copy of the registration, we need to clear the fields
     * TODO: Find a way to type it so we don't need the ts-ignore
     */
    if (clone.alias === 'registration' && clone.settings) {
      // @ts-ignore
      clone.settings.fields = [];
    }

    //Insert new addon on correct index
    columnData.addons.splice(addonIndex + 1, 0, clone);
    this.parse(columnData);

    await this.saveBlock();

    this.state.addons.forEach((addon, index) => {
      if (index === addonIndex + 1) {
        store.dispatch('editing/setActiveModel', {
          model: addon
        });
      }
    });
  }

  async onDelete() {
    await Promise.all(this.state.addons.map((addon) => addon.onDelete()));
  }

  //Delete column
  async delete() {
    if (this.deleted) {
      return;
    }

    this.deleted = true;

    await this.row.deleteColumn(this.index);
  }

  //Delete addon
  async deleteAddon(addonIndex: number) {
    await this.state.addons[Number(addonIndex)].onDelete();
    const data = this.getData();
    data.addons.splice(addonIndex, 1);
    this.parse(data);
    await this.saveBlock();
  }

  getAddonByIndex(index: number) {
    return this.state.addons[Number(index)];
  }

  async saveBlock() {
    return this.row.section.saveBlock();
  }

  reorderAddons() {
    const data = this.getData();
    data.addons = this.state.addons.map((addon) => {
      // TODO: Investigate if we can remove TS ignore. Not sure if this is possible.
      // @ts-ignore
      return addon.getData();
    });
    this.parse(data);
  }

  setNewAddonActive(newAddon: NewAddon) {
    store.dispatch('editing/setActiveModel', {
      model: newAddon.model
    });
  }

  /**
   * TO CONSIDER (From NC):
   * At some point we were able to add addons in between addons, which is why we have the "addonIndex"
   * This has been removed from ModalContentAddAddon.vue for now
   * This leads to the question if we should remove addonIndex from this function - I'll keep it for now, just in case we might need it later on
   */
  async addAddon(addonId: string, addonIndex: number | null) {
    const data = this.getData();
    const newAddonIndex = addonIndex || addonIndex === 0 ? addonIndex + 1 : null;
    const newAddonData = {
      settings: {},
      alias: addonId
    };

    let addonIndexInData: number | null = null;

    if (newAddonIndex || newAddonIndex === 0) {
      addonIndexInData = newAddonIndex;
      data.addons.splice(newAddonIndex, 0, newAddonData);
    } else {
      addonIndexInData = data.addons.length;
      data.addons.push(newAddonData);
    }

    await this.saveBlock();

    // In some addons like registration & sponsors we rely on data
    // that we receive from the server. So in these cases we need to reload
    // the section after saving it.
    await this.getSection().reload();

    return new Promise<ContentAddonType>((resolve) => {
      this.state.addons.find((addon, index) => {
        if (index === addonIndexInData) {
          this.setNewAddonActive({
            model: addon,
            id: Number(addon.state.id)
          });
          resolve(addon);
        }
      });
    });
  }

  private static checkForAddons(data: ContentColumnData, alias: string): boolean {
    let hasAddon = false;
    if (data.addons) {
      data.addons.some((addon) => {
        if (addon.alias === alias) {
          hasAddon = true;
        }
      });
    }
    return hasAddon;
  }

  private static parseCustomCSSData(data: ContentColumnData, identifier: string): CustomCSSModel | undefined {
    if (data.settings?.advanced?.customcss) {
      const useData = getDeviceData(data.settings.advanced.customcss) as CustomCSS | undefined;

      if (!useData) {
        return undefined;
      }
      if (useData.code && useData.code.length > 0) {
        return ColumnModel.constructCustomCSSState(useData, identifier);
      }
      return undefined;
    }
  }

  private static constructCustomCSSState(data: CustomCSS, identifier: string): CustomCSSModel {
    return new CustomCSSModel(data, identifier);
  }

  public shallSkipInitialParse(): boolean {
    return true;
  }

  public setEditingActive(): void {
    const state = this.state;
    if (state.edit) {
      state.edit.isActive = true;
      state.edit.isCollapsed = false;
    }

    if (this.row.state.edit) {
      this.row.state.edit.isCollapsed = false;
    }

    if (this.row.section.state.edit) {
      this.row.section.state.edit.isCollapsed = false;
    }
  }

  authorSignature(): string {
    return 'Nicky Christensen';
  }

  public getAddons<Model extends ContentAddonType>(alias: string): Model[] {
    const addonModels: Model[] = [];

    this.state.addons.forEach((addon) => {
      if (addon.alias === alias) {
        addonModels.push(addon as Model);
      }
    });

    return addonModels;
  }

  public get index() {
    return this.row.state.columns.indexOf(this);
  }

  public get rowIndex() {
    return this.row.index;
  }
}
